import { useState, useEffect, useRef, useCallback } from "react";
import { Button, ForgeLogo, ForgeTitle } from "../components/Primitives";
import storage from "../utils/storage";
import { useAuth } from "../context/AuthContext";
import { auth as authApi } from "../api/client";

// ─── HIGH-TECH PARTICLE FIELD ─────────────────────────────────
// Neural-network-style canvas with floating nodes + connection lines.
// GPU-accelerated, responsive, 60fps on iPhone and desktop.
function ParticleField({ accent = "#00E0A0" }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";

    // Spawn particles — density scales with screen area
    const count = Math.min(Math.floor((w * h) / 12000), 100);
    const particles = [];
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        a: Math.random() * 0.4 + 0.1,
        pulse: Math.random() * Math.PI * 2,
      });
    }
    particlesRef.current = particles;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Parse accent color to RGB for line drawing
    const m = accent.match(/[0-9a-f]{2}/gi);
    const rgb = m ? m.map(v => parseInt(v, 16)).join(",") : "0,224,160";
    const connDist = Math.min(w, h) * 0.15; // connection distance

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      const t = performance.now() * 0.001;

      // Update + draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;

        const pulseA = p.a * (0.7 + 0.3 * Math.sin(t * 0.8 + p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb},${pulseA})`;
        ctx.fill();
      }

      // Draw connections between close particles
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connDist) {
            const lineA = (1 - dist / connDist) * 0.12;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${rgb},${lineA})`;
            ctx.stroke();
          }
        }
      }

      animRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, [accent]);

  useEffect(() => {
    init();
    const handleResize = () => {
      cancelAnimationFrame(animRef.current);
      init();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute", inset: 0,
        pointerEvents: "none", zIndex: 0,
      }}
    />
  );
}

export default function LoginScreen({ C, onLogin, resetToken: initialResetToken }) {
  const { login, loading: authLoading, error: authError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [shake, setShake] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [stage, setStage] = useState(0);
  // Forgot / Reset password state
  const [view, setView] = useState(initialResetToken ? "reset" : "login"); // "login" | "forgot" | "reset" | "resetSent" | "setup" | "setupCode"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetTokenValue] = useState(initialResetToken || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);
  // Account setup state (SMS verification)
  const [setupData, setSetupData] = useState(null); // { hasPhone, maskedPhone }
  const [setupPhone, setSetupPhone] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [setupNewPassword, setSetupNewPassword] = useState("");
  const [setupConfirm, setSetupConfirm] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState(null);
  const [codeSent, setCodeSent] = useState(false);
  useEffect(() => {
    // Cinematic timing — each beat lands with purpose
    const t = [
      setTimeout(() => setStage(1), 50),    // bg atmosphere (instant, subtle)
      setTimeout(() => setStage(2), 300),    // logo materializes
      setTimeout(() => setStage(3), 1100),   // title + divider (logo has settled)
      setTimeout(() => setStage(4), 1800),   // subtitle + pillars
      setTimeout(() => setStage(5), 2400),   // description
      setTimeout(() => setStage(6), 3000),   // CTA
    ];
    return () => t.forEach(clearTimeout);
  }, []);

  const features = [
    { label: "YOUR DATA", desc: "Adapts to you" },
    { label: "24 / 7", desc: "Always available" },
    { label: "FROM $15", desc: "Per month" },
  ];

  return (
    <div style={{
      minHeight: "100vh", background: "#000000", fontFamily: "var(--b)", color: C.text1,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "48px 32px", textAlign: "center", position: "relative",
    }}>
      {/* ─── BACKGROUND — high-tech layered depth ─── */}

      {/* Live particle field — neural network nodes with connection lines */}
      <ParticleField accent={C.accent} />

      {/* Deep radial glow — forge furnace center */}
      <div style={{
        position: "absolute", top: "40%", left: "50%", width: 1000, height: 1000, borderRadius: "50%",
        background: `radial-gradient(circle, ${C.accent010} 0%, ${C.accent005} 20%, transparent 50%)`,
        transform: "translate(-50%, -50%)",
        opacity: stage >= 1 ? 1 : 0,
        transition: "opacity 3s cubic-bezier(0.16,1,0.3,1)",
        pointerEvents: "none",
      }} />

      {/* Secondary atmosphere — breathing orbital glow */}
      <div style={{
        position: "absolute", top: "20%", left: "50%", width: 600, height: 600, borderRadius: "50%",
        background: `radial-gradient(circle, ${C.accent008} 0%, transparent 60%)`,
        transform: "translate(-50%, -50%)",
        opacity: stage >= 2 ? 0.8 : 0,
        transition: "opacity 2.5s cubic-bezier(0.16,1,0.3,1)",
        animation: stage >= 2 ? "loginOrbBreathe 8s ease-in-out infinite" : "none",
        pointerEvents: "none",
      }} />

      {/* Noise/grain texture — adds film-grade depth */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        opacity: 0.025,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
      }} />

      {/* Scanning line — slow sweep for high-tech atmosphere */}
      <div style={{
        position: "absolute", left: 0, right: 0, height: 1,
        background: `linear-gradient(90deg, transparent 0%, ${C.accent015} 20%, ${C.accent030} 50%, ${C.accent015} 80%, transparent 100%)`,
        boxShadow: `0 0 20px ${C.accent020}, 0 0 60px ${C.accent008}`,
        opacity: stage >= 2 ? 0.6 : 0,
        animation: stage >= 2 ? "scanLine 6s linear infinite" : "none",
        pointerEvents: "none",
      }} />

      {/* Vertical light beam — centered behind logo */}
      <div style={{
        position: "absolute", top: 0, left: "50%", width: 1, height: "100%",
        background: `linear-gradient(180deg, transparent 10%, ${C.accent008} 35%, ${C.accent015} 50%, ${C.accent008} 65%, transparent 90%)`,
        transform: "translateX(-50%)",
        opacity: stage >= 2 ? 1 : 0,
        transition: "opacity 2s cubic-bezier(0.16,1,0.3,1) 0.3s",
        pointerEvents: "none",
      }} />

      {/* Corner tech brackets — top-left and bottom-right */}
      <svg style={{ position: "absolute", top: 24, left: 24, opacity: stage >= 3 ? 0.15 : 0, transition: "opacity 2s ease", pointerEvents: "none" }} width="40" height="40" viewBox="0 0 40 40">
        <path d="M0 12 L0 0 L12 0" fill="none" stroke={C.accent} strokeWidth="1" />
      </svg>
      <svg style={{ position: "absolute", bottom: 24, right: 24, opacity: stage >= 3 ? 0.15 : 0, transition: "opacity 2s ease", pointerEvents: "none" }} width="40" height="40" viewBox="0 0 40 40">
        <path d="M40 28 L40 40 L28 40" fill="none" stroke={C.accent} strokeWidth="1" />
      </svg>

      {/* Horizontal accent line — appears with logo */}
      <div style={{
        position: "absolute", top: "50%", left: 0, right: 0, height: 1,
        background: `linear-gradient(90deg, transparent 15%, ${C.accent005} 40%, ${C.accent008} 50%, ${C.accent005} 60%, transparent 85%)`,
        opacity: stage >= 3 ? 1 : 0,
        transition: "opacity 2s cubic-bezier(0.16,1,0.3,1)",
        pointerEvents: "none",
      }} />

      {/* ─── CONTENT ─── */}
      <div style={{ position: "relative", zIndex: 1, maxWidth: 360, width: "100%" }}>

        {/* Periodic Table Logo — materializes with glow bloom */}
        <div style={{
          opacity: stage >= 2 ? 1 : 0,
          transform: stage >= 2 ? "scale(1)" : "scale(0.85)",
          filter: stage >= 2 ? "blur(0px)" : "blur(12px)",
          transition: "opacity 1.2s cubic-bezier(0.16,1,0.3,1), transform 1.2s cubic-bezier(0.16,1,0.3,1), filter 1.4s cubic-bezier(0.16,1,0.3,1)",
          display: "flex", justifyContent: "center", marginBottom: 36,
          position: "relative",
        }}>
          {/* Glow bloom behind logo */}
          <div style={{
            position: "absolute", top: "50%", left: "50%", width: 140, height: 140,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.accent020} 0%, ${C.accent008} 40%, transparent 70%)`,
            transform: "translate(-50%, -50%)",
            opacity: stage >= 2 ? 1 : 0,
            transition: "opacity 1.5s ease 0.3s",
            animation: stage >= 3 ? "loginGlowPulse 4s ease-in-out infinite" : "none",
            pointerEvents: "none",
          }} />
          <ForgeLogo C={C} size="lg" />
        </div>

        {/* Title — ForgeTitle with Fe highlight */}
        <div style={{
          opacity: stage >= 3 ? 1 : 0,
          transform: stage >= 3 ? "translateY(0)" : "translateY(8px)",
          filter: stage >= 3 ? "blur(0px)" : "blur(4px)",
          transition: "opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1), filter 1s cubic-bezier(0.16,1,0.3,1)",
          marginBottom: 8, lineHeight: 1.1,
          letterSpacing: ".16em",
        }}>
          <ForgeTitle C={C} size={42} />
        </div>

        {/* Accent divider — grows from center with energy */}
        <div style={{
          height: 2, margin: "0 auto 18px",
          background: C.dividerGrad,
          boxShadow: `0 0 16px ${C.accent030}, 0 0 40px ${C.accent015}`,
          width: stage >= 3 ? 120 : 0,
          opacity: stage >= 3 ? 1 : 0,
          transition: "width 1.2s cubic-bezier(0.16,1,0.3,1) 0.3s, opacity 0.6s cubic-bezier(0.16,1,0.3,1) 0.3s",
        }} />

        {/* Subtitle — FITNESSFORGE.AI with neon glow */}
        <div style={{
          fontSize: 'var(--fs-sm)', color: C.accent, fontFamily: "var(--m)", letterSpacing: ".4em",
          fontWeight: 700, textTransform: "uppercase",
          textShadow: `0 0 20px ${C.accent040}, 0 0 60px ${C.accent020}`,
          opacity: stage >= 4 ? 1 : 0,
          transform: stage >= 4 ? "translateY(0)" : "translateY(4px)",
          transition: "opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)",
          marginBottom: 32,
          animation: stage >= 4 ? "loginTextGlow 4s ease-in-out infinite 1s" : "none",
        }}>
          fitnessforge.ai
        </div>

        {/* Feature pillars — 3 columns, stagger in */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 'var(--sp-md)',
          marginBottom: 24,
        }}>
          {features.map(({ label, desc }, i) => (
            <div key={label} style={{
              padding: "16px 8px",
              background: C.structGlass,
              border: `1px solid ${C.structBorder}`,
              borderRadius: 12,
              position: "relative", overflow: "hidden",
              backdropFilter: "blur(8px)",
              opacity: stage >= 4 ? 1 : 0,
              transform: stage >= 4 ? "translateY(0)" : "translateY(10px)",
              transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${0.15 * i}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${0.15 * i}s`,
            }}>
              {/* Top edge accent */}
              <div style={{
                position: "absolute", top: 0, left: "15%", right: "15%", height: 1,
                background: i === 1 ? C.dividerGrad : `linear-gradient(90deg, transparent, ${C.structBorderHover}, transparent)`,
                boxShadow: i === 1 ? `0 0 8px ${C.accent020}` : "none",
              }} />
              <div style={{
                fontSize: 'var(--fs-sm)', fontWeight: 700, color: i === 1 ? C.accent : C.text2,
                fontFamily: "var(--m)", letterSpacing: ".14em", marginBottom: 4,
                textShadow: i === 1 ? `0 0 16px ${C.accent040}` : "none",
              }}>{label}</div>
              <div style={{
                fontSize: 'var(--fs-xs)', color: C.text3, fontFamily: "var(--m)", letterSpacing: ".06em",
              }}>{desc}</div>
            </div>
          ))}
        </div>

        {/* Description */}
        <div style={{
          fontSize: 'var(--fs-md)', color: C.text2, lineHeight: 1.7, fontFamily: "var(--b)",
          opacity: stage >= 5 ? 1 : 0,
          transform: stage >= 5 ? "translateY(0)" : "translateY(6px)",
          transition: "opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)",
          maxWidth: 300, margin: "0 auto 28px",
        }}>
          The same coaching intelligence that drives elite results — adapted to your body, your goals, and your response to training. Available 24/7.
        </div>

        {/* CTA — Login / Forgot / Reset forms */}
        <div style={{
          opacity: stage >= 5 ? 1 : 0,
          transform: stage >= 5 ? "scale(1) translateY(0)" : "scale(0.96) translateY(8px)",
          transition: "opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)",
        }}>
          {/* ENTER FORGE button */}
          {!showLogin && view === "login" && (
            <Button C={C} onClick={() => setShowLogin(true)}>
              ENTER FORGE
            </Button>
          )}

          {/* Login form */}
          {showLogin && view === "login" && (
            <div style={{ animation: shake ? "loginShake 0.5s ease" : "loginFormReveal 0.4s ease" }}>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setLoginError(null);
                try {
                  const result = await login(email, password);
                  storage.set("lg", true);
                  onLogin(result?.client);
                } catch (err) {
                  // Account needs setup — switch to SMS verification flow
                  if (err.needsSetup) {
                    setSetupData({ hasPhone: err.hasPhone, maskedPhone: err.maskedPhone });
                    setView("setup");
                    return;
                  }
                  // Client login failed — try trainer login
                  try {
                    const trainerResult = await authApi.trainerLogin(email, password);
                    if (trainerResult.success) {
                      const base = window.location.pathname.match(/^(.*\/WebObjects\/forge\.woa)/);
                      window.location.href = base ? `${base[1]}/wa/trainerPortal` : '/wa/trainerPortal';
                      return;
                    }
                  } catch { /* trainer login also failed */ }
                  setLoginError(err.message || "Login failed");
                  setShake(true);
                  setTimeout(() => setShake(false), 500);
                }
              }} style={{ display: "flex", flexDirection: "column", gap: 'var(--sp-md)' }}>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address" autoComplete="email" required
                  style={inputStyle(C, loginError)} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password" autoComplete="current-password" required
                  style={inputStyle(C, loginError)} />
                {loginError && <div style={errorStyle(C)}>{loginError}</div>}
                <Button C={C} type="submit" disabled={authLoading}>
                  {authLoading ? "AUTHENTICATING..." : "SIGN IN"}
                </Button>
              </form>
              <div onClick={() => { setView("forgot"); setLoginError(null); setForgotEmail(email); }}
                style={linkStyle(C)}>
                FORGOT PASSWORD
              </div>
              <div onClick={() => { setShowLogin(false); setLoginError(null); }}
                style={{ ...linkStyle(C), marginTop: 8, color: C.text5 }}>
                BACK
              </div>
            </div>
          )}

          {/* Forgot password — enter email */}
          {view === "forgot" && (
            <div style={{ animation: "loginFormReveal 0.4s ease" }}>
              <div style={{ fontSize: 'var(--fs-md)', color: C.text2, marginBottom: 20, fontFamily: "var(--b)" }}>
                Enter your email and we'll send you a reset link.
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setForgotLoading(true);
                try {
                  await authApi.requestPasswordReset(forgotEmail);
                  setView("resetSent");
                } catch {
                  setView("resetSent"); // Always show success to prevent enumeration
                } finally {
                  setForgotLoading(false);
                }
              }} style={{ display: "flex", flexDirection: "column", gap: 'var(--sp-md)' }}>
                <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Email address" autoComplete="email" required
                  style={inputStyle(C, false)} autoFocus />
                <Button C={C} type="submit" disabled={forgotLoading}>
                  {forgotLoading ? "SENDING..." : "SEND RESET LINK"}
                </Button>
              </form>
              <div onClick={() => setView("login")} style={linkStyle(C)}>
                BACK TO SIGN IN
              </div>
            </div>
          )}

          {/* Reset link sent confirmation */}
          {view === "resetSent" && (
            <div style={{ animation: "loginFormReveal 0.4s ease" }}>
              <div style={{
                padding: "24px 20px", borderRadius: 14,
                background: C.structGlass, border: `1px solid ${C.accent030}`,
                backdropFilter: "blur(12px)",
              }}>
                <div style={{ fontSize: 'var(--fs-lg)', color: C.accent, fontFamily: "var(--m)",
                  letterSpacing: ".08em", fontWeight: 700, marginBottom: 12 }}>
                  CHECK YOUR EMAIL
                </div>
                <div style={{ fontSize: 'var(--fs-md)', color: C.text3, lineHeight: 1.8, fontFamily: "var(--b)" }}>
                  If an account exists for <span style={{ color: C.text1 }}>{forgotEmail}</span>,
                  you'll receive a password reset link. It expires in 1 hour.
                </div>
              </div>
              <div onClick={() => setView("login")} style={linkStyle(C)}>
                BACK TO SIGN IN
              </div>
            </div>
          )}

          {/* Account setup — send SMS code */}
          {view === "setup" && setupData && (
            <div style={{ animation: "loginFormReveal 0.4s ease" }}>
              <div style={{
                padding: "24px 20px", borderRadius: 14,
                background: C.structGlass, border: `1px solid ${C.accent030}`,
                backdropFilter: "blur(12px)", marginBottom: 20,
              }}>
                <div style={{ fontSize: 'var(--fs-lg)', color: C.accent, fontFamily: "var(--m)",
                  letterSpacing: ".08em", fontWeight: 700, marginBottom: 12 }}>
                  SET UP YOUR ACCOUNT
                </div>
                {setupData.hasPhone ? (
                  <>
                    <div style={{ fontSize: 'var(--fs-md)', color: C.text3, lineHeight: 1.8, fontFamily: "var(--b)" }}>
                      We'll send a verification code to your phone ending in{' '}
                      <span style={{ color: C.text1 }}>{setupData.maskedPhone?.slice(-4)}</span>
                    </div>
                    <div style={{ marginTop: 20 }}>
                      <Button C={C} disabled={setupLoading || codeSent} onClick={async () => {
                        setSetupLoading(true);
                        try {
                          await authApi.sendSetupCode(email);
                          setCodeSent(true);
                          setView("setupCode");
                        } catch {
                          setSetupError("Failed to send code. Please try again.");
                        } finally {
                          setSetupLoading(false);
                        }
                      }}>
                        {setupLoading ? "SENDING..." : "SEND CODE"}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 'var(--fs-md)', color: C.text3, lineHeight: 1.8, fontFamily: "var(--b)", marginBottom: 16 }}>
                      Enter your phone number to receive a verification code.
                    </div>
                    <input type="tel" value={setupPhone} onChange={(e) => setSetupPhone(e.target.value)}
                      placeholder="Phone number" autoComplete="tel" required autoFocus
                      style={inputStyle(C, setupError)} />
                    <div style={{ marginTop: 16 }}>
                      <Button C={C} disabled={setupLoading || !setupPhone.trim()} onClick={async () => {
                        setSetupLoading(true);
                        setSetupError(null);
                        try {
                          await authApi.sendSetupCode(email, setupPhone.trim());
                          setCodeSent(true);
                          setView("setupCode");
                        } catch {
                          setSetupError("Failed to send code. Please try again.");
                        } finally {
                          setSetupLoading(false);
                        }
                      }}>
                        {setupLoading ? "SENDING..." : "SEND CODE"}
                      </Button>
                    </div>
                  </>
                )}
                {setupError && <div style={{ ...errorStyle(C), marginTop: 12 }}>{setupError}</div>}
              </div>
              <div onClick={() => { setView("login"); setSetupData(null); setSetupError(null); }}
                style={linkStyle(C)}>
                BACK TO SIGN IN
              </div>
            </div>
          )}

          {/* Account setup — enter code + set password */}
          {view === "setupCode" && (
            <div style={{ animation: "loginFormReveal 0.4s ease" }}>
              <div style={{ fontSize: 'var(--fs-lg)', color: C.accent, fontFamily: "var(--m)",
                letterSpacing: ".08em", fontWeight: 700, marginBottom: 8 }}>
                VERIFY & SET PASSWORD
              </div>
              <div style={{ fontSize: 'var(--fs-md)', color: C.text3, marginBottom: 20, fontFamily: "var(--b)" }}>
                Enter the 6-digit code sent to your phone
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setSetupError(null);
                if (setupCode.length !== 6) {
                  setSetupError("Enter the 6-digit code");
                  return;
                }
                if (setupNewPassword.length < 4) {
                  setSetupError("Password must be at least 4 characters");
                  return;
                }
                if (setupNewPassword !== setupConfirm) {
                  setSetupError("Passwords do not match");
                  return;
                }
                setSetupLoading(true);
                try {
                  const data = await authApi.setupPassword(email, setupCode, setupNewPassword);
                  if (data.success) {
                    storage.set("lg", true);
                    onLogin(data?.client);
                  }
                } catch (err) {
                  setSetupError(err.message || "Invalid code or setup failed");
                } finally {
                  setSetupLoading(false);
                }
              }} style={{ display: "flex", flexDirection: "column", gap: 'var(--sp-md)' }}>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="6-digit code" autoComplete="one-time-code" required autoFocus
                  style={{ ...inputStyle(C, setupError), letterSpacing: ".3em", fontSize: "var(--fs-xl)" }} />
                <input type="password" value={setupNewPassword} onChange={(e) => setSetupNewPassword(e.target.value)}
                  placeholder="Choose a password" autoComplete="new-password" required
                  style={inputStyle(C, setupError)} />
                <input type="password" value={setupConfirm} onChange={(e) => setSetupConfirm(e.target.value)}
                  placeholder="Confirm password" autoComplete="new-password" required
                  style={inputStyle(C, setupError)} />
                {setupError && <div style={errorStyle(C)}>{setupError}</div>}
                <Button C={C} type="submit" disabled={setupLoading}>
                  {setupLoading ? "VERIFYING..." : "SET PASSWORD"}
                </Button>
              </form>
              <div onClick={async () => {
                setSetupLoading(true);
                try { await authApi.sendSetupCode(email); } catch {}
                setSetupLoading(false);
                setSetupError("New code sent");
              }} style={linkStyle(C)}>
                RESEND CODE
              </div>
              <div onClick={() => { setView("login"); setSetupData(null); setSetupError(null);
                setSetupCode(""); setSetupNewPassword(""); setSetupConfirm(""); setCodeSent(false); }}
                style={{ ...linkStyle(C), marginTop: 8, color: C.text5 }}>
                BACK TO SIGN IN
              </div>
            </div>
          )}

          {/* Reset password form (from email link) */}
          {view === "reset" && (
            <div style={{ animation: "loginFormReveal 0.4s ease" }}>
              <div style={{ fontSize: 'var(--fs-lg)', color: C.accent, fontFamily: "var(--m)",
                letterSpacing: ".08em", fontWeight: 700, marginBottom: 20 }}>
                SET NEW PASSWORD
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setResetError(null);
                if (newPassword.length < 4) {
                  setResetError("Password must be at least 4 characters");
                  return;
                }
                if (newPassword !== confirmPassword) {
                  setResetError("Passwords do not match");
                  return;
                }
                setResetLoading(true);
                try {
                  const data = await authApi.resetPassword(resetTokenValue, newPassword);
                  if (data.success) {
                    storage.set("lg", true);
                    onLogin(data?.client);
                  }
                } catch (err) {
                  setResetError(err.message || "Reset failed. The link may have expired.");
                } finally {
                  setResetLoading(false);
                }
              }} style={{ display: "flex", flexDirection: "column", gap: 'var(--sp-md)' }}>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password" autoComplete="new-password" required
                  style={inputStyle(C, resetError)} autoFocus />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password" autoComplete="new-password" required
                  style={inputStyle(C, resetError)} />
                {resetError && <div style={errorStyle(C)}>{resetError}</div>}
                <Button C={C} type="submit" disabled={resetLoading}>
                  {resetLoading ? "RESETTING..." : "SET PASSWORD"}
                </Button>
              </form>
              <div onClick={() => setView("login")} style={linkStyle(C)}>
                BACK TO SIGN IN
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 56,
          opacity: stage >= 6 ? 1 : 0, transition: "opacity 1.5s ease 0.4s",
        }}>
          <div style={{
            height: 1, width: 30, background: C.dividerGrad,
            margin: "0 auto 14px", opacity: 0.4,
            boxShadow: `0 0 8px ${C.accent015}`,
          }} />
          <div style={{
            fontSize: 'var(--fs-2xs)', color: C.text5, fontFamily: "var(--m)", letterSpacing: ".25em",
          }}>
            fitnessforge.ai
          </div>
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes scanLine {
          0% { top: -2%; }
          100% { top: 102%; }
        }
        @keyframes loginOrbBreathe {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          50% { transform: translate(-50%, -50%) scale(1.12); opacity: 1; }
        }
        @keyframes loginGlowPulse {
          0%, 100% { opacity: 0.7; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
        }
        @keyframes loginTextGlow {
          0%, 100% { text-shadow: 0 0 20px ${C.accent040}, 0 0 60px ${C.accent020}; }
          50% { text-shadow: 0 0 30px ${C.accent060}, 0 0 80px ${C.accent030}, 0 0 120px ${C.accent015}; }
        }
        @keyframes loginFormReveal {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes loginShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}

function inputStyle(C, hasError) {
  return {
    width: "100%", padding: "14px 18px",
    background: C.structGlass,
    border: `1.5px solid ${hasError ? (C.warn || '#ff6b6b') : C.structBorderHover}`,
    borderRadius: 12, color: C.text1, fontSize: 'var(--fs-lg)',
    fontFamily: "var(--b)", outline: "none",
    textAlign: "center", letterSpacing: ".02em",
    transition: "border-color 0.3s", backdropFilter: "blur(8px)",
  };
}

function errorStyle(C) {
  return {
    fontSize: 'var(--fs-body)', color: C.warn || '#ff6b6b', fontFamily: "var(--m)",
    letterSpacing: ".04em",
  };
}

function linkStyle(C) {
  return {
    marginTop: 16, cursor: "pointer",
    fontSize: 'var(--fs-sm)', color: C.text4, fontFamily: "var(--m)",
    letterSpacing: ".1em",
  };
}
